import { jsx, type JsxElementProps, onConnect } from '@amazon/vinyl-tsx'
import {
    combineData,
    data,
    type MutableValue,
    type ObservableValue,
} from '@amazon/vinyl-observable'
import { Icon } from '@/components/Icon'
import { createDisposer } from '@amazon/vinyl-util'
import type { AmznTrackType } from '@amzn/vinyl-amzn'
import type { Player } from '@/playback/api'
import { getString } from '@/nerdStats/i18n/bundle.generated'
import {
    type DeviceAudioInfo,
    getDeviceAudioInfo,
    getOutputAudioInfo,
} from '@/nerdStats/getDeviceAudioInfo'
import { Modal } from '@/components/Modal'
import { navigateTo } from '@/router/router'

export interface NerdStatsDeps {
    readonly player: Player
}

export type NerdStatsProps = JsxElementProps<'div'> & {
    visible: MutableValue<boolean>
    deps: NerdStatsDeps
}

interface QualityValues {
    readonly bitDepth: string
    readonly sampleRate: string
}

const samplingRateFormatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
})

function formatSampleRate(hz: number | null | undefined, fallback: string): string {
    if (!hz) return fallback
    return `${samplingRateFormatter.format(hz / 1000)} kHz`
}

function formatBitDepth(depth: number | null | undefined, fallback: string): string {
    if (!depth) return fallback
    return `${depth}-bit`
}

function isStandardTier(trackType: AmznTrackType | null | undefined): boolean {
    return trackType === 'SD'
}

function qualityTierName(
    trackType: AmznTrackType | null | undefined,
    standardLabel: string
): string {
    if (!trackType || trackType === 'UNKNOWN') return ''
    if (trackType === 'SD') return standardLabel
    if (trackType === 'UHD') return 'ULTRA HD'
    return trackType
}

function qualityDescriptionKey(
    trackType: AmznTrackType | null | undefined
):
    | 'dmusic_nerd_stats_quality_description_uhd'
    | 'dmusic_nerd_stats_quality_description_hd'
    | 'dmusic_nerd_stats_quality_description_sd'
    | null {
    switch (trackType) {
        case 'UHD':
            return 'dmusic_nerd_stats_quality_description_uhd'
        case 'HD':
            return 'dmusic_nerd_stats_quality_description_hd'
        case 'SD':
            return 'dmusic_nerd_stats_quality_description_sd'
        default:
            return null
    }
}

/**
 * Stats for Nerds panel — sectioned audio quality breakdown ported
 * from green hornet's StatsForNerdsModal. Three rows: track quality
 * (from the player's playbackQualityAmzn), device (browser sample
 * rate via Web Audio API), and output device (mediaDevices probe).
 *
 * The browser can't expose true bit depth, so device/output rows
 * infer it from the AudioContext sample rate — matches hornet web.
 */
export function NerdStats(props: NerdStatsProps) {
    const { player } = props.deps
    const trackType = data<AmznTrackType | null>(null)
    const trackBitDepth = data<number | null>(null)
    const trackSampleRate = data<number | null>(null)
    const browserInfo = data(getDeviceAudioInfo())
    const outputInfo = data<DeviceAudioInfo | null>(null)

    function refreshTrackQuality() {
        const q = player.playbackQualityAmzn
        trackType.value = q?.trackType ?? null
        trackBitDepth.value = q?.bitDepth ?? null
        trackSampleRate.value = q?.samplingRate ?? null
    }

    const tierName = trackType.map((t) =>
        qualityTierName(t, getString('dmusic_nerd_stats_quality_tier_standard').value)
    )
    const description = trackType.map((t) => {
        const key = qualityDescriptionKey(t)
        return key ? getString(key).value : ''
    })

    const trackQualityValues = combineData({
        t: trackType,
        bd: trackBitDepth,
        sr: trackSampleRate,
    }).map(({ t, bd, sr }): QualityValues | null => {
        if (!t) return null
        const standardLabel = getString('dmusic_nerd_stats_quality_tier_standard').value
        const unknownLabel = getString('dmusic_nerd_stats_value_unknown').value
        if (isStandardTier(t)) return { bitDepth: standardLabel, sampleRate: standardLabel }
        return {
            bitDepth: formatBitDepth(bd, unknownLabel),
            sampleRate: formatSampleRate(sr, unknownLabel),
        }
    })

    const deviceQualityValues = combineData({ t: trackType, info: browserInfo }).map(
        ({ t, info }): QualityValues => {
            const standardLabel = getString('dmusic_nerd_stats_quality_tier_standard').value
            const unknownLabel = getString('dmusic_nerd_stats_value_unknown').value
            if (t && isStandardTier(t)) {
                return { bitDepth: standardLabel, sampleRate: standardLabel }
            }
            return {
                bitDepth: formatBitDepth(info.bitDepth, unknownLabel),
                sampleRate: formatSampleRate(info.sampleRate, unknownLabel),
            }
        }
    )

    const outputQualityValues = combineData({ t: trackType, info: outputInfo }).map(
        ({ t, info }): QualityValues => {
            const standardLabel = getString('dmusic_nerd_stats_quality_tier_standard').value
            const unknownLabel = getString('dmusic_nerd_stats_value_unknown').value
            const loadingLabel = getString('dmusic_nerd_stats_value_loading').value
            if (t && isStandardTier(t)) {
                return { bitDepth: standardLabel, sampleRate: standardLabel }
            }
            if (!info) return { bitDepth: loadingLabel, sampleRate: loadingLabel }
            return {
                bitDepth: formatBitDepth(info.bitDepth, unknownLabel),
                sampleRate: formatSampleRate(info.sampleRate, unknownLabel),
            }
        }
    )

    const trackSectionTitle = tierName.map(
        (n) => `${getString('dmusic_nerd_stats_section_title_track_quality').value} ${n}`
    )
    const deviceSectionTitle = browserInfo.map(
        (info) => `${getString('dmusic_nerd_stats_section_title_device').value} ${info.deviceName}`
    )
    const outputSectionTitle = outputInfo.map((info) => {
        const fallback = getString('dmusic_nerd_stats_output_device_default').value
        const prefix = getString('dmusic_nerd_stats_section_title_output').value
        return `${prefix} ${info?.deviceName ?? fallback}`
    })

    const view = (
        <Modal
            {...props}
            className={`nerdStats ${props.className ?? ''}`}
            visible={props.visible}
            onClose={() => {
                props.visible.value = false
            }}
        >
            <div className="header">
                <h2>{getString('dmusic_nerd_stats_modal_title_audio_quality')}</h2>
            </div>

            <div className="qualityDescription">
                <div className="qualityTierName">{tierName}</div>
                <div className="qualityDescriptionText">{description}</div>
            </div>

            <div className="divider" />

            <NerdStatsSection icon="song" title={trackSectionTitle} values={trackQualityValues} />
            <StreamingSettingsRow
                qualityTierName={tierName}
                onClick={() => {
                    props.visible.value = false
                    navigateTo('/streamingQualitySettings')
                }}
            />
            <NerdStatsSection
                icon="computer"
                title={deviceSectionTitle}
                values={deviceQualityValues}
            />
            <NerdStatsSection
                icon="myMusic"
                title={outputSectionTitle}
                values={outputQualityValues}
            />
        </Modal>
    )

    onConnect(view, () => {
        const { add, dispose } = createDisposer()
        // Track quality refreshes on player events + when the panel
        // toggles visible (so a stale value doesn't linger after a
        // skip while the panel was hidden).
        add(player.on('playbackQualityChange', refreshTrackQuality))
        add(
            props.visible.onData((visible) => {
                if (visible) {
                    refreshTrackQuality()
                    // Lazy-load the output device list — first open
                    // costs a navigator.mediaDevices round-trip, but
                    // we don't need it before the panel is open.
                    if (outputInfo.value === null) {
                        getOutputAudioInfo()
                            .then((info) => {
                                outputInfo.value = info
                            })
                            .catch(() => {
                                // keep "loading" state on failure —
                                // the panel will simply not surface
                                // an output line. Matches hornet.
                            })
                    }
                }
            })
        )
        return dispose
    })
    return view
}

interface NerdStatsSectionProps {
    readonly icon: Parameters<typeof Icon>[0]['icon']
    readonly title: ObservableValue<string>
    readonly values: ObservableValue<QualityValues | null>
}

function NerdStatsSection(props: NerdStatsSectionProps) {
    return (
        <div className="nerdStatsSection">
            <div className="nerdStatsSectionHeader">
                <Icon icon={props.icon} className="nerdStatsSectionIcon" />
                <span className="nerdStatsSectionTitle">{props.title}</span>
            </div>
            <div className="nerdStatsSectionSpecs">
                <span>{props.values.map((v) => v?.bitDepth ?? '')}</span>
                <span className="nerdStatsSpecDivider">·</span>
                <span>{props.values.map((v) => v?.sampleRate ?? '')}</span>
            </div>
        </div>
    )
}

interface StreamingSettingsRowProps {
    readonly qualityTierName: ObservableValue<string>
    readonly onClick: () => void
}

/**
 * Mid-timeline row that opens the audio quality settings page.
 * Mirrors hornet's StreamingSettingsRow — sits between Track Quality
 * and Device with a gear icon on the dotted connector and a
 * caret-right glyph on the right edge to signal navigation.
 */
function StreamingSettingsRow(props: StreamingSettingsRowProps) {
    return (
        <button
            className="nerdStatsSection nerdStatsStreamingSettingsRow"
            type="button"
            aria-label={props.qualityTierName.map(
                (n) => `${getString('dmusic_nerd_stats_streaming_settings_prefix').value} ${n}`
            )}
            title={getString('dmusic_nerd_stats_streaming_settings_hint')}
            onclick={() => props.onClick()}
        >
            <div className="nerdStatsSectionHeader">
                <Icon icon="settings" className="nerdStatsSectionIcon" />
                <span className="nerdStatsSectionTitle">
                    <span className="streamingSettingsLabel">
                        {getString('dmusic_nerd_stats_streaming_settings_prefix')}
                    </span>{' '}
                    <span className="streamingSettingsValue">{props.qualityTierName}</span>
                </span>
                <Icon icon="caretRight" className="streamingSettingsCaret" />
            </div>
        </button>
    )
}
